import { FormGroup, FormArray, FormBuilder } from "@angular/forms";
import { Input, OnInit, OnDestroy, Component, Output, EventEmitter } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { ItemService } from "../services/item.service";
import { StreamService } from "../services/stream.service";
import { first, takeUntil, debounceTime, distinctUntilChanged } from "rxjs/operators";
import * as _ from 'lodash';

@Component({
    selector: 'filter-builder',
    template: `
        <form [formGroup]="form">
            <div fxLayout="row">
                <mat-form-field style="min-width: 400px;">
                    <input matInput placeholder="Keywords" formControlName="basicFilterText">
                </mat-form-field>
                <mat-form-field style="min-width: 400px; padding-left:20px;">
                    <mat-select placeholder="Match Type" formControlName="matchType" fxFlex>
                        <mat-option value="IncludeAllKeywords">Include All Keywords</mat-option>
                        <mat-option value="IncludeAnyKeywords">Include Any Keywords</mat-option>
                        <mat-option value="MatchExactPhrase">Match Exact Phrase</mat-option>
                    </mat-select>
                </mat-form-field>
                <div fxFlex fxFlexOffset="5">
                    <mat-spinner *ngIf="noTouchingCount" diameter="15"></mat-spinner>
                    <h3 *ngIf="!noTouchingCount">{{totalItems}} Total Items</h3>
                </div>
                <span fxFlex></span>
                <span [matTooltip]="hasEmptyGroups() ? 'Remove empty tag group before adding another' : 'Add another tag group'">
                    <button mat-raised-button color="primary" (click)="addTagGroup()" [disabled]="hasEmptyGroups()">Add Tag Group</button>
                </span>
            </div>
            <basic-filter-editor [basicFilterStructures]="form.get('basicFilterStructures')"
                                 [availableTags]="availableTags"
                                 [keyTags]="keyTags"
                                 [docCount]="docCount"
                                 [disabled]="noTouchingCount"
                                 (tagAdded)="fetchTotalItemsAndTagCounts()"
                                 (tagGroupRemoved)="fetchTotalItemsAndTagCounts()"
                                 (tagRemoved)="fetchTotalItemsAndTagCounts()"></basic-filter-editor>

        </form>
    `
})
export class FilterBuilderComponent implements OnInit, OnDestroy {
    @Input() form: FormGroup;
    @Output() countChanged = new EventEmitter<number>();
    @Output() sourcesChanged = new EventEmitter<any>();
    private cleanupTime = new Subject<void>();
    totalItems: any;    
    noTouchingCount;
    availableTags = [];
    keyTags = [];
    docCount
    
    constructor(private fb: FormBuilder,
                private itemService: ItemService,
                private streamService: StreamService) {}
    
    ngOnInit(): void {
        this.clearStructures();
        this.availableTags = [];
        this.keyTags = [];
        this.form.get('basicFilterText').valueChanges
        .pipe(
            takeUntil(this.cleanupTime),
            debounceTime(1000),
            distinctUntilChanged()
        ).subscribe(() => { this.fetchAvailableTags(); });

        this.form.get('advancedFilterTerm').valueChanges
        .pipe(
            takeUntil(this.cleanupTime),
            debounceTime(1000),
            distinctUntilChanged()
        ).subscribe(() => { this.fetchAvailableTags(); });
        
        this.form.get('matchType').valueChanges
        .pipe(
            takeUntil(this.cleanupTime),
            debounceTime(1000),
            distinctUntilChanged()
        ).subscribe(() => { this.fetchAvailableTags(); });
        
        this.addTagGroup();
        this.fetchAvailableTags();
    }
    
    private doSearch(): Observable<any> {
        let search = this.form.get('isAdvanced').value ? this.form.get('advancedFilterTerm').value : this.streamService.basicFilterTermFromFilter(this.form.value);
        return this.itemService.searchItemsWithSettings(
            {
                search: search?.trim().length ? search : undefined,
                searchConfiguration: { filterStructures: this.form.value.basicFilterStructures }
            },
            {  includeAggregations: true, includeReadItems: true }
        );
    }

    clearStructures(): void {
        let basicFilterStructures = <FormArray> this.form.get('basicFilterStructures');
        while (basicFilterStructures.length !== 0) {
            basicFilterStructures.removeAt(0)
        }
    }

    addTagGroup(): void {
    const basicFilterStructures = <FormArray> this.form.get('basicFilterStructures');
        basicFilterStructures.push(this.fb.group({
            condition: this.fb.control('CONTAINS'),
            type: this.fb.control('TAG'),
            values: this.fb.array([])
        }));
    }

    hasEmptyGroups(): boolean {
        return  this.form.value.basicFilterStructures.length > 0 && !this.form.value.basicFilterStructures.filter(basicStruct => basicStruct.values.length).length;
    }

    fetchAvailableTags(): void {
        this.noTouchingCount = true;
        this.doSearch().pipe(first()).subscribe(searchRes => {
            this.totalItems = searchRes.totalElements;
            this.countChanged.emit(searchRes.totalElements);
            this.sourcesChanged.emit(searchRes.aggregations ? searchRes.aggregations.streamIds.buckets : {});
            this.noTouchingCount = false;

            this.keyTags = [];
            this.docCount = searchRes.aggregations ? searchRes.aggregations.tagTypes.doc_count : 0;
            this.availableTags = _(searchRes.aggregations ? searchRes.aggregations.tagTypes.type.buckets : [])
            .map(bucket => {
                this.keyTags.push(bucket.key);
                return _.map(bucket.tagText.buckets, tag => _.extend({}, tag, { type: bucket.key, tagText: tag.key }))
            })
            .flatten()
            .sortBy(['doc_count'])
            .reverse()
            .value();
        }); 
    }

    fetchTotalItemsAndTagCounts(): void {
        this.noTouchingCount = true;
        this.doSearch().pipe(first()).subscribe(searchRes => {
            this.totalItems = searchRes.totalElements;
            this.countChanged.emit(searchRes.totalElements);
            this.sourcesChanged.emit(searchRes.aggregations.streamIds.buckets);
            this.noTouchingCount = false;
            this.docCount = searchRes.aggregations.tagTypes.doc_count;
            const updatedTags = _(searchRes.aggregations.tagTypes.type.buckets)
                .map(bucket =>  _.map(bucket.tagText.buckets, tag => _.extend({}, tag, { type: bucket.key, tagText: tag.key })))
                .flatten()
                .keyBy(item => item.type + item.key)
                .value();
            this.availableTags.forEach(tag => {
                const updated = updatedTags[tag.type + tag.key];
                tag.doc_count = updated ? updated.doc_count : 0;
            });
        });
    }

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
}